/**
 * Документы админки — разметка 1:1 с legacy htdocs/core/templates/admin_docs.tpl
 * и рекурсивным admin_docs_inner.tpl: дерево .jExplorer (ul/li, li.document, li.fadd,
 * li.dadd), формы .daddform поверх подложки .fBG, кнопки .ebtn/.dbtn, сообщения
 * .bs-callout (docs.min.css).
 *
 * Поведение legacy-скриптов сохранено: клик по .showForm показывает форму и подложку,
 * клик по .fBG прячет их; клик по названию папки раскрывает/сворачивает вложенный
 * список (jExplorer.js оборачивал название в <a href=#> и делал slideToggle —
 * здесь тот же <a> и тот же класс isClose).
 *
 * API: GET /api/admin/content/docs, POST/PUT/DELETE .../docs/folders[/{id}],
 * POST/PUT/DELETE .../docs/files[/{id}] (Handlers\Admin\Content).
 * Файл скачивается по legacy-URL /docs/get/{id}/{filename}.
 */

import { useRef, useState } from 'react'

import { api, ApiError } from '@/api/client'
import { useLegacyCss } from '@/lib/legacyAssets'
import { useApi } from '@/lib/useApi'
import { isTrue } from '@/lib/format'

/** Узел дерева docs (legacy Settings_model::get_docs): у корня id='0', top_folder='00'. */
interface DocsNode {
  id: number | string
  name: string
  filename?: string
  is_folder?: boolean | 't' | 'f'
  top_folder?: number | string
  folders?: DocsNode[]
  files?: DocsNode[]
}

/** Какая форма .daddform сейчас раскрыта (в legacy — showForm/fBG). */
interface OpenForm {
  kind: 'folder-rename' | 'file-rename' | 'folder-add' | 'file-add'
  id: number
}

export function AdminDocsPage() {
  useLegacyCss('admin', 'newapproach', 'docs.min', 'jExplorer')

  const { data, reload } = useApi<DocsNode>('/api/admin/content/docs')

  const [ok, setOk] = useState<string[]>([])
  const [error, setError] = useState<string[]>([])
  const [openForm, setOpenForm] = useState<OpenForm | null>(null)

  const showForm = (form: OpenForm): void => setOpenForm(form)
  const hideForms = (): void => setOpenForm(null)

  /** Общий обработчик ответа: legacy показывал сообщения в bs-callout сверху. */
  const run = async (action: () => Promise<{ message?: string }>): Promise<void> => {
    try {
      const result = await action()
      setOk(result.message === undefined ? [] : [result.message])
      setError([])
      hideForms()
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка операции'])
      setOk([])
    }
  }

  return (
    <>
      {error.length > 0 && (
        <div className="bs-callout bs-callout-danger">
          <h4>Ошибка</h4>
          {error.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
        </div>
      )}
      {ok.length > 0 && (
        <div className="bs-callout bs-callout-info">
          <h4>OK</h4>
          {ok.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
        </div>
      )}

      <div className="jExplorer">
        <ul>
          {data !== null && (
            <DocsInner
              docdata={data}
              openForm={openForm}
              showForm={showForm}
              run={run}
            />
          )}
        </ul>
      </div>
      <div className="fBG" style={{ display: openForm === null ? 'none' : 'block' }} onClick={hideForms} />
    </>
  )
}

interface InnerProps {
  docdata: DocsNode
  openForm: OpenForm | null
  showForm: (form: OpenForm) => void
  run: (action: () => Promise<{ message?: string }>) => Promise<void>
}

/** Рекурсивный узел (legacy admin_docs_inner.tpl). */
function DocsInner({ docdata, openForm, showForm, run }: InnerProps) {
  const id = Number(docdata.id)
  const isRoot = id === 0
  // legacy jExplorer: вложенные ul отрисованы с классом isClose (display:none) и
  // раскрываются кликом по названию папки; корневой ul всегда открыт
  const [open, setOpen] = useState(isRoot)

  const [folderName, setFolderName] = useState(docdata.name)
  const [newFolderName, setNewFolderName] = useState('')
  const [fileName, setFileName] = useState('')
  const fileInput = useRef<HTMLInputElement>(null)

  const isOpenForm = (kind: OpenForm['kind']): boolean =>
    openForm !== null && openForm.kind === kind && openForm.id === id

  /** legacy file-submit[{top_folder}]: multipart с несколькими файлами. */
  const uploadFiles = (): void => {
    const files = fileInput.current?.files
    void run(async () => {
      const form = new FormData()
      form.append('top_folder', String(id))
      form.append('name', fileName)
      for (const file of files === null || files === undefined ? [] : Array.from(files)) {
        form.append('file[]', file)
      }
      return api.upload<{ message: string }>('/api/admin/content/docs/files', form)
    })
    setFileName('')
  }

  return (
    <li>
      {id > 0 && (
        <>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              setOpen(!open)
            }}
          >
            {docdata.name}
          </a>
          <button
            className="showForm ebtn"
            onClick={(e) => {
              e.preventDefault()
              showForm({ kind: 'folder-rename', id })
            }}
          >
            Переименовать
          </button>
          <form
            method="POST"
            className="daddform"
            style={{ display: isOpenForm('folder-rename') ? 'block' : 'none' }}
            onSubmit={(e) => {
              e.preventDefault()
              void run(() =>
                api.put<{ message: string }>(`/api/admin/content/docs/folders/${id}`, {
                  name: folderName,
                }),
              )
            }}
          >
            <span>Изменение названия папки</span>
            <input
              placeholder="Новое название папки"
              value={folderName}
              className=""
              type="text"
              name={`folder_name[${id}]`}
              onChange={(e) => setFolderName(e.target.value)}
            />
            <input
              className="btn btn-xs btn-success"
              type="submit"
              name={`folder-rename[${id}]`}
              value="Изменить имя"
            />
          </form>
          <form
            method="POST"
            className="delfrm"
            onSubmit={(e) => {
              e.preventDefault()
              void run(() =>
                api.delete<{ message: string }>(`/api/admin/content/docs/folders/${id}`),
              )
            }}
          >
            <input className="dbtn" type="submit" name={`folder-delete[${id}]`} value="Удалить папку" />
          </form>
        </>
      )}

      <ul className={id > 0 && !open ? 'isClose' : undefined}>
        {(docdata.folders ?? []).map((folder) => (
          <DocsInner
            key={folder.id}
            docdata={folder}
            openForm={openForm}
            showForm={showForm}
            run={run}
          />
        ))}

        {(docdata.files ?? []).map((file) => (
          <FileItem key={file.id} file={file} openForm={openForm} showForm={showForm} run={run} />
        ))}

        <li className="fadd">
          <a
            className="showForm"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              showForm({ kind: 'folder-add', id })
            }}
          >
            Добавить папку
          </a>
          <form
            method="POST"
            className="daddform"
            style={{ display: isOpenForm('folder-add') ? 'block' : 'none' }}
            onSubmit={(e) => {
              e.preventDefault()
              void run(() =>
                api.post<{ message: string }>('/api/admin/content/docs/folders', {
                  top_folder: id,
                  name: newFolderName,
                }),
              )
              setNewFolderName('')
            }}
          >
            <span>Добавление папки</span>
            <input
              placeholder="Название папки"
              className=""
              type="text"
              name={`folder_name[${id}]`}
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
            />
            <input
              className="btn btn-xs btn-success"
              type="submit"
              name={`folder-submit[${id}]`}
              value="Создать папку"
            />
          </form>
        </li>

        <li className="dadd">
          <a
            className="showForm"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              showForm({ kind: 'file-add', id })
            }}
          >
            Загрузить документ
          </a>
          <form
            method="POST"
            className="daddform"
            encType="multipart/form-data"
            style={{ display: isOpenForm('file-add') ? 'block' : 'none' }}
            onSubmit={(e) => {
              e.preventDefault()
              uploadFiles()
            }}
          >
            <span>Загрузка документа</span>
            <input className="" type="file" multiple name="file[]" ref={fileInput} />
            <input
              placeholder="Название документа"
              className=""
              type="text"
              name={`file_name[${id}]`}
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
            />
            <input
              className="btn btn-xs btn-success"
              type="submit"
              name={`file-submit[${id}]`}
              value="Загрузить файл"
            />
          </form>
        </li>
      </ul>
    </li>
  )
}

interface FileProps {
  file: DocsNode
  openForm: OpenForm | null
  showForm: (form: OpenForm) => void
  run: (action: () => Promise<{ message?: string }>) => Promise<void>
}

/** Файл в дереве (legacy li.document). */
function FileItem({ file, openForm, showForm, run }: FileProps) {
  const id = Number(file.id)
  const [name, setName] = useState(file.name)

  const formOpen =
    openForm !== null && openForm.kind === 'file-rename' && openForm.id === id

  // is_folder приходит как 't'/'f' — на всякий случай отсекаем папки, попавшие в files
  if (isTrue(file.is_folder ?? 'f')) return null

  return (
    <li className="document">
      <a
        href={`/docs/get/${id}/${file.filename ?? ''}`}
        // Скачиваем с заголовком Authorization: по обычной ссылке браузер его не пошлёт
        // (см. api/client.ts). Админ и житель ходят сюда с разными токенами.
        onClick={(event) => {
          event.preventDefault()
          void api.downloadFile(`/docs/get/${id}/${file.filename ?? ''}`, file.filename ?? file.name)
        }}
      >
        {file.name}
      </a>
      <button
        className="showForm ebtn"
        onClick={(e) => {
          e.preventDefault()
          showForm({ kind: 'file-rename', id })
        }}
      >
        Переименовать
      </button>
      <form
        method="POST"
        className="daddform"
        style={{ display: formOpen ? 'block' : 'none' }}
        onSubmit={(e) => {
          e.preventDefault()
          void run(() =>
            api.put<{ message: string }>(`/api/admin/content/docs/files/${id}`, { name }),
          )
        }}
      >
        <span>Изменение названия файла</span>
        <input
          placeholder="Новое название файла"
          value={name}
          className=""
          type="text"
          name={`file_name[${id}]`}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          className="btn btn-xs btn-success"
          type="submit"
          name={`file-rename[${id}]`}
          value="Изменить имя"
        />
      </form>
      <form
        method="POST"
        className=""
        onSubmit={(e) => {
          e.preventDefault()
          void run(() => api.delete<{ message: string }>(`/api/admin/content/docs/files/${id}`))
        }}
      >
        <input className="dbtn" type="submit" name={`file-delete[${id}]`} value="Удалить файл" />
      </form>
    </li>
  )
}
